import React, { useEffect, useRef, useState } from 'react';
import { useXpGain } from '../../hooks/useXpGain';
import { useUserStats } from '../../hooks/useUserStats';
import { useCategories } from '../../hooks/useCategories';
import { useTheme, METANOIA_GRADIENT_BAR } from '../../hooks/useTheme';
import { useProgressDisplayStore, type ProgressDisplayMode } from '../../stores/progressDisplayStore';

// Sequence timings (ms)
const FADE_IN = 260;
const FADE_OUT = 550;
const FILL_DELAY = 320;   // wait after fade-in before animating the gain
const FILL_DUR = 850;     // fill duration
const DOMAIN_HOLD = 600;  // hold before the domain line fades (persistent modes)
const HOLD = 950;         // hold before the whole group fades (transient modes)

const XP_KEYFRAME: Record<string, string> = {
  slide: 'xp-fly',
  rise: 'xp-rise',
  pop: 'xp-pop',
};

// At least 2 digits, except 0 and 100+ (so 2→"02", 9→"09", 0→"0", 100→"100").
function pad2(n: number): string {
  const v = Math.max(0, Math.round(isNaN(n) ? 0 : n));
  return v === 0 || v >= 100 ? String(v) : String(v).padStart(2, '0');
}

interface HeaderRewardProps {
  mode: ProgressDisplayMode;
}

/**
 * Mobile top-bar progress + XP reward layer (all modes except 'none').
 *
 * On every XP gain: a "+N XP" chip reveals (style per settings), the user bar
 * animates the gain, and a very thin domain-level line animates just beneath it.
 *
 * - bar/both: user bar is persistent; only the domain line + chip are transient.
 * - ring/dynamic: the whole group is transient and fades in/out **collectively**
 *   (single opacity, domain line rides the group) so the exit reads as one fade.
 */
export function HeaderReward({ mode }: HeaderRewardProps) {
  const gain = useXpGain();
  const { stats } = useUserStats();
  const { getCategoryColor } = useCategories();
  const { primaryColorCSS, isMetanoia } = useTheme();
  const xpStyle = useProgressDisplayStore((s) => s.xpStyle);
  const xpPosition = useProgressDisplayStore((s) => s.xpPosition);

  const userPersistent = mode === 'bar' || mode === 'both';

  // Transient group visibility (ring/dynamic).
  const [celebrating, setCelebrating] = useState(false);
  // Domain line own visibility (persistent modes only).
  const [domVisible, setDomVisible] = useState(false);

  const [userPct, setUserPct] = useState(stats.progress);
  const [userLevel, setUserLevel] = useState(stats.level);
  const [domPct, setDomPct] = useState(0);
  const [domColor, setDomColor] = useState<string | null>(null);
  const [chip, setChip] = useState<{ amount: number; token: number } | null>(null);

  const timers = useRef<number[]>([]);
  const rafs = useRef<number[]>([]);
  const clearAll = () => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
    rafs.current.forEach((r) => cancelAnimationFrame(r));
    rafs.current = [];
  };
  useEffect(() => () => clearAll(), []);

  const animate = (from: number, to: number, dur: number, set: (v: number) => void) => {
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      set(from + (to - from) * eased);
      if (t < 1) rafs.current.push(requestAnimationFrame(step));
    };
    rafs.current.push(requestAnimationFrame(step));
  };

  useEffect(() => {
    if (!gain) return;
    clearAll();

    setChip({ amount: gain.amount, token: gain.token });
    setDomColor(gain.domainId ? getCategoryColor(gain.domainId) : null);
    setDomPct(gain.domainFrom);

    if (userPersistent) {
      setDomVisible(true);
    } else {
      setUserLevel(stats.level);
      setUserPct(gain.userFrom);
      setCelebrating(true);
    }

    const domTo = gain.domainLeveledUp ? 100 : gain.domainTo;
    const userTo = gain.userLeveledUp ? 100 : gain.userTo;

    timers.current.push(
      window.setTimeout(() => {
        animate(gain.domainFrom, domTo, FILL_DUR, setDomPct);
        if (!userPersistent) animate(gain.userFrom, userTo, FILL_DUR, setUserPct);
      }, FILL_DELAY)
    );

    // Safety finals (covers throttled rAF).
    timers.current.push(
      window.setTimeout(() => {
        setDomPct(domTo);
        if (!userPersistent) setUserPct(userTo);
      }, FILL_DELAY + FILL_DUR)
    );

    // Exit: persistent → fade only the domain line; transient → fade the whole group.
    if (userPersistent) {
      timers.current.push(
        window.setTimeout(() => setDomVisible(false), FILL_DELAY + FILL_DUR + DOMAIN_HOLD)
      );
    } else {
      timers.current.push(
        window.setTimeout(() => setCelebrating(false), FILL_DELAY + FILL_DUR + HOLD)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gain?.token]);

  const liveUserPct = Math.max(0, Math.min(100, isNaN(stats.progress) ? 0 : stats.progress));
  const shownUserPct = userPersistent ? liveUserPct : Math.max(0, Math.min(100, userPct));
  const shownUserLevel = userPersistent ? stats.level : userLevel;

  // Group opacity: persistent stays visible; transient fades in/out collectively.
  const groupVisible = userPersistent || celebrating;
  // Domain line opacity: persistent fades on its own; transient rides the group (always 1).
  const domainOpacity = userPersistent ? (domVisible ? 1 : 0) : 1;

  const barFill = isMetanoia ? METANOIA_GRADIENT_BAR : primaryColorCSS;
  const xpAnimation = `${XP_KEYFRAME[xpStyle] || 'xp-fly'} 1100ms ease-out forwards`;
  const xpPosClass =
    xpPosition === 'center' ? 'left-1/2 -translate-x-1/2'
    : xpPosition === 'right' ? 'right-9'
    : 'left-9';

  return (
    <div className="relative flex w-full items-center">
      {/* "+N XP" chip — sits BELOW the domain bar; position + reveal style per settings. */}
      {chip && chip.amount > 0 && (
        <span
          key={chip.token}
          className={`pointer-events-none absolute top-full z-10 mt-1 ${xpPosClass}`}
        >
          <span
            className="xp-chip block whitespace-nowrap text-xs font-semibold italic"
            style={{ color: domColor || 'rgb(var(--primary))', animation: xpAnimation }}
          >
            +{chip.amount} XP
          </span>
        </span>
      )}

      <div
        className="flex w-full items-center gap-2"
        style={{
          opacity: groupVisible ? 1 : 0,
          transition: `opacity ${groupVisible ? FADE_IN : FADE_OUT}ms ease-out`,
        }}
        aria-hidden={!groupVisible}
      >
        <span className="shrink-0 text-xs font-medium text-muted-foreground">Lvl {pad2(shownUserLevel)}</span>

        <div className="relative flex-1">
          {/* User-level bar. The gradient spans the full width and is revealed left→right
              via clip-path, so the colour ramp is static (cyan→purple→orange) regardless
              of fill % — at 5% you see only the cyan start, not all three colours. */}
          <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background: barFill,
                clipPath: `inset(0 ${100 - shownUserPct}% 0 0 round 9999px)`,
                transition: userPersistent ? 'clip-path 700ms ease-out' : 'none',
              }}
            />
          </div>

          {/* Domain-level line — absolute, a hair below the user bar (adds no height) */}
          <div
            className="absolute left-0 right-0 top-full mt-1 h-0.5 overflow-hidden rounded-full bg-muted/50"
            style={{
              opacity: domainOpacity,
              transition: `opacity ${domainOpacity ? FADE_IN : FADE_OUT}ms ease-out`,
            }}
            aria-hidden
          >
            <div
              className="h-full rounded-full"
              style={{ width: `${Math.max(0, Math.min(100, domPct))}%`, background: domColor || barFill }}
            />
          </div>
        </div>

        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{pad2(shownUserPct)}%</span>
      </div>
    </div>
  );
}
